/**
 * chartSpec — the ONE normalization seam between the two shapes a chart
 * payload arrives in and the single resolved shape `Chart` renders.
 *
 * Why this exists
 * ---------------
 * The K2 `chart` block is emitted by the runtime (`ais-agent-runtime`
 * `render_chart.py`) and validated/persisted by the BE
 * (`AISquare-Studio-BE` `chart_block.py`, the "BE#2577 flat contract"): the
 * block's `props` carry the chart FLAT — `chart_type`, `x.key`,
 * `series[].key`, `value_format`, `summary`, ... — NOT wrapped in a `spec`.
 * The renderer historically took a nested recharts-shaped `spec`
 * (`type` / `xKey` / `showLegend`), so a real emitted block did not render
 * (EPIC #188). Rather than teach every render branch two field names, this
 * module resolves EITHER shape into one internal `ResolvedChartSpec` and the
 * renderer reads only that.
 *
 * Contract posture (matches `chart_block.py`): never throw. Junk input — a
 * non-object, a series that is not an array, rows that are not objects —
 * degrades to an empty resolved spec, which `Chart` renders as its muted
 * empty state. The BE guarantees enum validity on real emitted blocks; the
 * fallbacks here only guard legacy / out-of-contract callers.
 *
 * Parity note: the value/tick formatting + palette semantics are PORTED from
 * AIStudio-v3's production `chart-format.ts` (its bespoke flat-contract
 * renderer), so a future v3 swap onto this package renderer is visually
 * invisible. Do not re-derive these semantics — change them in lockstep with
 * v3 or not at all.
 *
 * Shape detection
 * ---------------
 * - `chart_type` present (or an `x` object carrying a string `key`) → the
 *   flat BE#2577 contract.
 * - otherwise → the legacy nested `ChartSpec` (`type` / `xKey`).
 * - a `{ spec }` wrapper is unwrapped at the `ChartBlock` level
 *   (`BlockRenderer`), never here.
 *
 * Defaults differ per shape on purpose: the nested shape keeps its
 * long-published defaults (`showLegend: false`); the flat shape enables the
 * legend whenever it is load-bearing (multi-series, or a pie — slices are
 * unidentifiable without one) because the flat contract has no legend field.
 */

/** Chart family. The full BE#2577 set (`chart_block.py` `CHART_TYPES`). */
export type ChartType = "bar" | "line" | "area" | "pie" | "scatter";

/** Bar orientation (`chart_block.py` `ORIENTATIONS`; bar only, default vertical). */
export type ChartOrientation = "vertical" | "horizontal";

/** X-axis kind (`chart_block.py` `X_AXIS_TYPES`; default category). */
export type ChartXAxisType = "category" | "time" | "number";

/**
 * How axis ticks / tooltip values are formatted (`chart_block.py`
 * `VALUE_FORMATS`). See `formatChartValue` for the exact semantics.
 */
export type ChartValueFormat = "integer" | "currency" | "percent" | "float";

export interface ChartSeries {
  /** Row key this series reads its y-value from. */
  key: string;
  /** Legend / tooltip label. Defaults to `key`. */
  label?: string;
  /**
   * Stroke / fill override. Defaults to the palette slot for this series.
   * `null` (what the BE emits for "use the theme palette") reads as unset.
   */
  color?: string | null;
}

/** The flat contract's x-axis descriptor (`props.x`). */
export interface ChartXAxis {
  /** Row key plotted on the x-axis. Required by the BE contract. */
  key: string;
  /** Axis caption. Default `""`. */
  label?: string;
  /** Axis kind. Default `"category"`. Load-bearing for `scatter` (numeric x). */
  type?: ChartXAxisType;
}

/**
 * The legacy nested chart spec — the renderer's original public shape
 * (`props.spec` of a legacy `chart` block, or a direct `<Chart spec={…} />`
 * consumer). Still fully supported. The optional variant fields introduced
 * with the flat contract (`orientation` / `stacked` / `donut` / `valueFormat`
 * / `summary` / `sourceNote`) are also accepted here so both shapes carry the
 * same capabilities.
 */
export interface ChartSpec {
  /** Chart family. Default `"bar"`. */
  type?: ChartType;
  /** Row objects to plot. */
  data: Array<Record<string, unknown>>;
  /** Row key plotted on the x-axis. */
  xKey: string;
  /** X-axis kind. Default `"category"`. Load-bearing for `scatter`. */
  xType?: ChartXAxisType;
  /** Series to draw. */
  series: ChartSeries[];
  /** Palette applied in series order; `series.color` wins per series. */
  colors?: string[];
  /** Render the legend. Default `false`. */
  showLegend?: boolean;
  /** Render the cartesian grid. Default `true`. */
  showGrid?: boolean;
  /** Optional title shown above the chart. */
  title?: string;
  /** Optional description shown under the title. */
  description?: string;
  /** Bar orientation. Default `"vertical"`. */
  orientation?: ChartOrientation;
  /** Stack a multi-series bar/area chart. Default `false`. */
  stacked?: boolean;
  /** Render a pie as a donut (non-zero inner radius). Default `false`. */
  donut?: boolean;
  /** Tick / tooltip number formatting. Unset = raw recharts ticks. */
  valueFormat?: ChartValueFormat;
  /** Accessible one-line description of what the chart shows. */
  summary?: string;
  /** Provenance line rendered as a muted footer. */
  sourceNote?: string;
}

/**
 * The flat BE#2577 chart-block props — EXACTLY what the runtime emits and the
 * BE validates/persists as the `chart` block's `props` (`chart_block.py`
 * docstring, spec §2). `Chart` accepts this directly, and `BlockRenderer`'s
 * `ChartBlock` routes a flat block's `props` here untouched.
 *
 * `donut` is a RENDERER-SIDE extension, not part of the BE contract: the BE's
 * normalization drops unknown props ("the contract is closed"), so a `donut`
 * flag survives only on payloads that do not round-trip through the BE
 * relay/persist path (e.g. host-constructed blocks or direct `<Chart>` use).
 */
export interface ChartBlockProps {
  chart_type: ChartType;
  title: string;
  subtitle?: string;
  x: ChartXAxis;
  series: ChartSeries[];
  data: Array<Record<string, unknown>>;
  value_format: ChartValueFormat;
  /** REQUIRED by the BE contract — a11y text + graceful fallback. */
  summary: string;
  /** Bar only. The BE stamps the default `"vertical"`. */
  orientation?: ChartOrientation;
  /** Multi-series bar/area. The BE stamps the default `false`. */
  stacked?: boolean;
  source_note?: string;
  /** Renderer-side donut flag (see note above — the BE strips unknown props). */
  donut?: boolean;
}

/** One resolved series: label filled, `null` color normalized away. */
export interface ResolvedChartSeries {
  key: string;
  label: string;
  color?: string;
}

/**
 * The single internal shape `Chart` renders — both input shapes resolve to
 * this. Every field is present (defaults filled), so render branches never
 * re-derive defaults.
 */
export interface ResolvedChartSpec {
  /**
   * The renderable chart family, or `undefined` when the input named a type
   * this renderer does not know (e.g. a future contract addition reaching an
   * old client). `Chart` degrades an unknown type to its summary/empty state
   * — silently drawing unknown-type data as a bar would misrepresent it.
   */
  type: ChartType | undefined;
  data: Array<Record<string, unknown>>;
  xKey: string;
  xLabel?: string;
  xType: ChartXAxisType;
  series: ResolvedChartSeries[];
  colors?: string[];
  showLegend: boolean;
  showGrid: boolean;
  title?: string;
  description?: string;
  summary?: string;
  sourceNote?: string;
  orientation: ChartOrientation;
  stacked: boolean;
  donut: boolean;
  valueFormat?: ChartValueFormat;
}

const CHART_TYPES: readonly ChartType[] = ["bar", "line", "area", "pie", "scatter"];
const X_AXIS_TYPES: readonly ChartXAxisType[] = ["category", "time", "number"];
const VALUE_FORMATS: readonly ChartValueFormat[] = ["integer", "currency", "percent", "float"];
const ORIENTATIONS: readonly ChartOrientation[] = ["vertical", "horizontal"];

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function asChartType(value: unknown): ChartType | undefined {
  return CHART_TYPES.includes(value as ChartType) ? (value as ChartType) : undefined;
}

function asXAxisType(value: unknown): ChartXAxisType | undefined {
  return X_AXIS_TYPES.includes(value as ChartXAxisType) ? (value as ChartXAxisType) : undefined;
}

function asValueFormat(value: unknown): ChartValueFormat | undefined {
  return VALUE_FORMATS.includes(value as ChartValueFormat)
    ? (value as ChartValueFormat)
    : undefined;
}

function asOrientation(value: unknown): ChartOrientation | undefined {
  return ORIENTATIONS.includes(value as ChartOrientation) ? (value as ChartOrientation) : undefined;
}

/** Rows must be plain objects; anything else is dropped, never thrown on. */
function readData(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is Record<string, unknown> =>
      !!row && typeof row === "object" && !Array.isArray(row),
  );
}

/** Entries need a non-empty string `key`; label defaults to it, `null` color reads as unset. */
function readSeries(value: unknown): ResolvedChartSeries[] {
  if (!Array.isArray(value)) return [];
  const resolved: ResolvedChartSeries[] = [];
  for (const entry of value) {
    const rec = asRecord(entry);
    const key = readString(rec?.key);
    if (!key) continue;
    resolved.push({
      key,
      label: readString(rec?.label) ?? key,
      color: readString(rec?.color),
    });
  }
  return resolved;
}

/**
 * True iff `input` is (or claims to be) the flat BE#2577 chart-block props:
 * a `chart_type` string, or an `x` object carrying a string `key`. The legacy
 * nested `ChartSpec` (`type` / `xKey`) matches neither.
 */
export function isFlatChartProps(input: unknown): input is ChartBlockProps {
  const rec = asRecord(input);
  if (!rec) return false;
  if (typeof rec.chart_type === "string") return true;
  const x = asRecord(rec.x);
  return typeof x?.key === "string";
}

/**
 * Resolve EITHER chart shape — the flat BE#2577 block props or the legacy
 * nested `ChartSpec` — into the one internal `ResolvedChartSpec`.
 *
 * Pure and total: never throws, never returns `undefined`. Junk input (or a
 * junk field inside otherwise-valid input) degrades field-by-field toward the
 * documented defaults; fully-junk input yields an empty spec that `Chart`
 * renders as its empty state. An off-enum `chart_type` / `type` resolves to
 * `type: undefined` (→ the summary/empty state, matching v3's fallback for a
 * future chart family reaching an old client); an ABSENT nested `type` keeps
 * its long-documented default `"bar"`.
 */
export function resolveChartSpec(input: unknown): ResolvedChartSpec {
  const rec = asRecord(input);

  if (rec && isFlatChartProps(rec)) {
    const x = asRecord(rec.x);
    const type = asChartType(rec.chart_type);
    const series = readSeries(rec.series);
    return {
      type,
      data: readData(rec.data),
      xKey: readString(x?.key) ?? "",
      xLabel: readString(x?.label),
      xType: asXAxisType(x?.type) ?? "category",
      series,
      colors: undefined,
      // The flat contract has no legend field; show it exactly when it is
      // load-bearing — several series, or pie (slices need naming).
      showLegend: series.length > 1 || type === "pie",
      showGrid: true,
      title: readString(rec.title),
      description: readString(rec.subtitle),
      summary: readString(rec.summary),
      sourceNote: readString(rec.source_note),
      orientation: asOrientation(rec.orientation) ?? "vertical",
      stacked: rec.stacked === true,
      donut: rec.donut === true,
      valueFormat: asValueFormat(rec.value_format),
    };
  }

  // Legacy nested `ChartSpec` (also the junk fallback: every field degrades
  // to its documented default, ending at the empty spec).
  const colors = Array.isArray(rec?.colors)
    ? (rec?.colors ?? []).filter((c): c is string => typeof c === "string")
    : undefined;
  return {
    // Absent -> the documented default "bar"; present-but-unknown -> degrade.
    type: rec?.type === undefined ? "bar" : asChartType(rec.type),
    data: readData(rec?.data),
    xKey: readString(rec?.xKey) ?? "",
    xLabel: undefined,
    xType: asXAxisType(rec?.xType) ?? "category",
    series: readSeries(rec?.series),
    colors: colors?.length ? colors : undefined,
    showLegend: rec?.showLegend === true,
    showGrid: rec?.showGrid !== false,
    title: readString(rec?.title),
    description: readString(rec?.description),
    summary: readString(rec?.summary),
    sourceNote: readString(rec?.sourceNote),
    orientation: asOrientation(rec?.orientation) ?? "vertical",
    stacked: rec?.stacked === true,
    donut: rec?.donut === true,
    valueFormat: asValueFormat(rec?.valueFormat),
  };
}

/* ============================================================================
 * value_format → display string
 *
 * PORTED from AIStudio-v3 `chart-format.ts` (`formatValue` / `formatAxisTick`
 * / `truncateLabel`) so the package renderer and v3's production renderer are
 * value-for-value identical. The only addition is the optional `locale`
 * parameter (v3 always uses the host locale; passing one keeps tests
 * deterministic) — behavior is unchanged when it is omitted.
 * ========================================================================= */

const numberFormatCache = new Map<string, Intl.NumberFormat>();

function cachedNumberFormat(
  locale: string | undefined,
  key: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const cacheKey = `${locale ?? ""}|${key}`;
  let format = numberFormatCache.get(cacheKey);
  if (!format) {
    format = new Intl.NumberFormat(locale, options);
    numberFormatCache.set(cacheKey, format);
  }
  return format;
}

/**
 * Format a numeric value for tooltips (and the full-precision end of axis
 * ticks) per the block's `value_format`:
 *
 * - `integer`  — grouped, no decimals (`1,234`).
 * - `currency` — **USD**, grouped, no decimals (`$1,234`). The contract
 *   carries no currency code, so USD is assumed (documented on `Chart`).
 * - `percent`  — the value is treated as ALREADY in percent units (the
 *   contract convention: the runtime emits `42`, not `0.42` — neither
 *   `chart_block.py` nor `render_chart.py` pins a scale, so this matches
 *   v3's shipped semantics), rendered with up to one decimal (`42.5%`).
 * - `float`    — grouped, up to two decimals (`1,234.5`).
 *
 * Never throws: a non-number passes through as its label (`null`/`undefined`
 * → `""`); a non-finite number renders as `""` rather than a literal
 * `NaN`/`Infinity` tick.
 */
export function formatChartValue(
  value: unknown,
  format?: ChartValueFormat,
  locale?: string,
): string {
  if (typeof value !== "number") {
    // Category/string values (or a stray null) pass through as their label.
    return value == null ? "" : String(value);
  }
  if (!Number.isFinite(value)) return "";
  switch (format) {
    case "currency":
      return cachedNumberFormat(locale, "currency", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);
    case "percent":
      return `${cachedNumberFormat(locale, "percent", { maximumFractionDigits: 1 }).format(value)}%`;
    case "float":
      return cachedNumberFormat(locale, "float", { maximumFractionDigits: 2 }).format(value);
    case "integer":
    default:
      return cachedNumberFormat(locale, "integer", { maximumFractionDigits: 0 }).format(value);
  }
}

/**
 * Compact axis-tick formatter — like {@link formatChartValue} but abbreviates
 * large magnitudes (`1.2K`, `3.4M`) so a dense value axis stays readable.
 * Currency keeps its symbol; percent keeps its sign. Values under 1000 fall
 * back to the full format.
 */
export function formatAxisTick(value: unknown, format?: ChartValueFormat, locale?: string): string {
  if (typeof value !== "number") {
    return value == null ? "" : String(value);
  }
  if (!Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  if (abs < 1000) return formatChartValue(value, format, locale);

  const units: Array<[number, string]> = [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (const [factor, suffix] of units) {
    if (abs >= factor) {
      const scaled = value / factor;
      const body = cachedNumberFormat(locale, "tick", { maximumFractionDigits: 1 }).format(scaled);
      if (format === "currency") return `$${body}${suffix}`;
      if (format === "percent") return `${body}${suffix}%`;
      return `${body}${suffix}`;
    }
  }
  return formatChartValue(value, format, locale);
}

/**
 * Truncate a long category label for an axis tick / pie-slice label so dense
 * axes don't collide. The full label still shows in the tooltip.
 * Whitespace-trimmed; appends an ellipsis only when it actually shortened the
 * text.
 */
export function truncateLabel(label: unknown, max = 16): string {
  const text = label == null ? "" : String(label);
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}
